<template>
  <div>
    <v-container>
      <v-row>
        <v-col cols="4">
          <k-card-header :title="title" :items="selectedProduct ? productHeaderItems : productsHeaderItems" />
        </v-col>
        <v-col cols="5">
            <v-select v-if="selectedProduct" :loading="isLoading()" v-model="selectedProduct" label="Product Versions"
                       ref="productSelect" outlined dense hide-details return-object :items="versionedProducts" :disabled="!versionedProducts || versionedProducts.length === 0">
              <template slot="selection" slot-scope="data">
                <!-- HTML that describe how select should render selected items -->
                {{ getDescription(data.item) }}
              </template>
              <template slot="item" slot-scope="data">
                <!-- HTML that describe how select should render items when the select is open -->
                {{ getDescription(data.item) }}
              </template>
            </v-select>
        </v-col>
          <!-- Edit button -->
        <v-col cols="2">
            <v-btn
                color="primary"
                ref="editButtonAtm"
                class="pl-3 pr-3 mr-2 mb-1"
                @click="onSetEditable()"
                :disabled="isEditable || createProductPressed || isLoading() || !versionedProducts || versionedProducts.length === 0"
                v-if="selectedProduct"
            >Edit
            </v-btn>
          <!-- Create button -->
            <v-btn
                color="primary"
                ref="createButtonAtm"
                class="pl-3 pr-3 mb-1"
                @click="onCreateNewProductVersion()"
                :disabled="createProductPressed || isLoading()"
                v-if="selectedProduct"
            >Create
            </v-btn>
        </v-col>
        <v-col cols="1" >
          <div class="float-right">
            <k-card-header-btn
                v-if="selectedProduct"
                label="Back"
                icon="fa-undo"
                :click-action="viewAllProducts"
            />
          </div>
        </v-col>
      </v-row>
    </v-container>

    <v-container v-if="isLoading()">
      <v-progress-circular indeterminate size="50" />
    </v-container>

    <v-container v-if="!isLoading()">
      <v-row>
        <v-col cols="12">
          <v-card class="detailCard">

              <template v-if="selectedProduct">

                <v-tabs
                  slider-size="4"
                  height="58"
                  v-model="selectedTab"
                >
                  <v-tab>
                    PRODUCT SETTINGS
                  </v-tab>
                  <v-tab :disabled="!selectedProduct.id">
                    LEVEL REWARDS
                  </v-tab>
                </v-tabs>

                <v-tabs-items v-model="selectedTab" class="scrollableCard">
                  <v-tab-item
                    eager
                  >
                    <product-settings
                      ref="productSettings"
                      :product="selectedProduct"
                      @dirtyForm="onProductSettingsDirty"
                      @saved="onProductSettingsSaved"
                      @deleted="onProductSettingsDeleted"
                      @reset="onReset()"
                      :versionedProducts="versionedProducts"
                      :unassignedProductMappings="unassignedProductMappings"
                      :isEditable="isEditable"
                      :isDirty="isDirty()"
                      :loading="isLoading()"/>
                  </v-tab-item>

                  <v-tab-item
                    eager
                  >
                    <level-rewards
                      ref="levelRewards"
                      :product="selectedProduct"
                      @dirtyForm="onLevelRewardsDirty"
                      @saved="onLevelRewardsSaved"
                      @reset="onReset()"
                      :isEditable="isEditable"
                      :isDirty="isDirty()"
                      />
                  </v-tab-item>
                </v-tabs-items>
              </template>
              <v-container v-else>
                <v-data-table
                  :headers="productHeaders"
                  :items="tableItems"
                  :loading="isLoading()"
                  :hide-default-footer="true"
                  :items-per-page="-1"
                  fixed-header
                  ref="productsTable">
                  <template v-slot:item="{ item }">
                    <tr>
                      <td>
                        <v-btn icon text @click="onEditProduct(item.product)">
                          <v-icon>fa-edit</v-icon>
                        </v-btn>
                      </td>
                      <td>{{ item.product.productMapping.productType }}</td>
                      <td>{{ item.product.productMapping.fiProductName }}</td>
                      <td>
                          <div v-for="coreProductCode in getUniqueCoreProductCodesForProduct(item.product.productMapping.coreAccountProductTypes)"
                            :key="coreProductCode">
                             {{ coreProductCode }}
                        </div>
                      </td>
                      <td>{{ getFriendlyFormattedDate(item.product.launchDate) }}</td>
                      <td>{{ getYesNo(item.product.active) }}</td>
                      <td>
                        <v-icon color="success" v-if="item.setup">fa-check</v-icon>
                        <v-icon color="error" v-if="item.setup===false">fa-times</v-icon>
                      </td>

                    </tr>
                  </template>
                </v-data-table>

                <!-- Add new button -->
                <v-col>
                  <v-tooltip bottom :disabled="thereAreUnassignedProductMappings()">
                    <template v-slot:activator="{ on }">
                      <div v-on="on" class="d-inline-block">
                        <v-btn color="info" @click="onNewProduct()" :disabled="!thereAreUnassignedProductMappings()" ref="createNewProductButton">
                          <v-icon class="pr-1">fa-plus-circle</v-icon>
                            Add New Product
                        </v-btn>
                      </div>
                    </template>
                    <span>There are currently no unassigned product mappings to create a new product.  <br/>
                    Edit a specific product above or create a new product mapping first.
                    </span>
                  </v-tooltip>
                </v-col>
              </v-container>

          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component, Inject, Watch,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { Mutation } from 'vuex-class';
import { Route } from 'vue-router';
import { DataTableHeader } from 'vuetify';
import { KCardHeader, KCardHeaderBtn } from '@kasasa/fbase-components';
import { Alert, NoticeClass, Dialog } from '@kasasa/fbase-components/lib';
import {
  Product,
  ProductResource,
  ProductValidationResult,
} from '@/api/product';
import ProductSettings from '@/components/products/ProductSettings.vue';
import LevelRewards from '@/components/products/LevelRewards.vue';


import {
  CoreAccountProduct, ProductMapping, ProductMappingResource,
} from '@/api/productmapping';
import { DateTool } from '@/util';
import { ConfigVersion, ConfigVersionDateRange } from '@/api/configversion';

interface ProductTableItem {
	product: Product,
	setup: boolean | null,
}

@Component({
  computed: mapState(['fiId', 'productValidationResults', 'allConfigVersions', 'configVersion']),
  components: {
    KCardHeader, KCardHeaderBtn, ProductSettings, LevelRewards,
  },
})
export default class Products extends Vue {
  @Inject('ProductResource')
  productResource!: ProductResource;

  @Inject('ProductMappingResource')
  productMappingResource!: ProductMappingResource;

  fiId!: string;

  allConfigVersions!: ConfigVersion[];

  configVersion!: ConfigVersion;

  productValidationResults!:ProductValidationResult[];

  loadedFiId: string | null = null;

  loadingCurrentProducts: boolean = false;

  loadingVersionProducts: boolean = false;

  loadingMappings: boolean = false;

  allCurrentProductsInCoreVersion: Product[] = [];

  unassignedProductMappings: ProductMapping[] = [];

  tableItems: ProductTableItem[] = [];

  selectedProduct: Product|null = null;

  editProductId: string | null = null;

  selectedTab: number | null = null;

  versionedProducts: Product[] = [];

  mappedProducts: Map<number, Product[]> = new Map();

  currentDesignIndex: number = 0;

  createProductPressed: boolean = false;

  title: string = 'Products';

  productSettingsDirty: boolean = false;

  levelRewardsDirty: boolean = false;

  isEditable: boolean = false;

  configVersionDateRange: ConfigVersionDateRange | null = null;

  @Mutation('productValidationResults') mutationProductValidationResults: any;

  productHeaders: DataTableHeader[] = [
    { text: '', value: 'edit', sortable: false },
    { text: 'Product Type', value: 'type', sortable: false },
    { text: 'Product Name', value: 'name', sortable: false },
    { text: 'Product Code(s)', value: 'code', sortable: false },
    { text: 'Launch Date', value: 'launchDate', sortable: false },
    { text: 'Active', value: 'active', sortable: false },
    { text: 'Setup', value: 'setup', sortable: false },
  ];

  productsHeaderItems: any = [
    {
      text: 'Rewards Configuration',
      disabled: false,
      link: false,
    },
    {
      text: 'Products',
      disabled: true,
      link: false,
    },
  ];

  productHeaderItems: any = [
    {
      text: 'Rewards Configuration',
      disabled: true,
      link: false,
    },
    {
      text: 'Products',
      disabled: false,
      link: true,
    },
    {
      text: 'Product',
      disabled: true,
      link: false,
    },
  ];

  mounted() {
    if (this.configVersion && this.allConfigVersions) {
      this.configVersionDateRange = ConfigVersionDateRange.ofVersion(this.configVersion, this.allConfigVersions);
    }
  }

  @Watch('$route', { immediate: true, deep: true })
  onRouteChange() {
    this.selectedProduct = null;
    this.isEditable = false;
    this.title = 'Products';
    const editMode:boolean = this.$route.name === 'product' || this.$route.name === 'newproduct';
    if (this.fiId !== this.loadedFiId) {
      this.productHeaderItems[1].href = `#/${this.$route.params.clientId}/products`;
      this.loadVersionedProducts().then(() => {
        this.loadProducts(editMode).then(() => { this.loadProductMappings(); });
      });
    } else if (editMode) {
      this.editProduct();
    } else {
      this.versionedProducts = [];
    }
  }

  @Watch('selectedProduct', { immediate: true, deep: true })
  onSelectedProductChange() {
    if (this.selectedProduct) {
      if (this.selectedProduct.id) {
        this.createProductPressed = false;
        this.isEditable = false;
      } else {
        this.createProductPressed = true;
        this.isEditable = true;
      }
    }
    this.levelRewardsDirty = false;
    this.productSettingsDirty = false;
  }

  @Watch('productValidationResults')
  updateTableItemsWithValidationResults() {
    this.tableItems.forEach((item: ProductTableItem) => {
      /* eslint-disable no-param-reassign */
      item.setup = this.getProductSetupStatus(item.product);
   	  /* eslint-enable no-param-reassign */
    });
  }

  onReset() {
    (this.$refs.levelRewards as LevelRewards).onProductChange();
    (this.$refs.productSettings as ProductSettings).onProductChange();
    this.levelRewardsDirty = false;
    this.productSettingsDirty = false;
  }

  onLevelRewardsSaved(savedProduct: Product) {
    this.levelRewardsDirty = false;
    this.productSaveOccurred(savedProduct);
  }

  onProductSettingsSaved(savedProduct: Product) {
    this.productSettingsDirty = false;
    this.productSaveOccurred(savedProduct);
  }

  onProductSettingsDeleted() {
    this.productSettingsDirty = false;
    this.loadVersionedProducts().then(() => {
      this.loadProducts(false).then(() => { this.loadProductMappings(); });
      this.validateProducts();
    })
      .finally(() => { this.viewAllProducts(); });
  }

  onProductSettingsDirty(isDirty: boolean) {
    this.productSettingsDirty = isDirty;
  }

  onLevelRewardsDirty(isDirty: boolean) {
    this.levelRewardsDirty = isDirty;
  }

  onSetEditable() {
    this.isEditable = true;
  }

  loadProductMappings() {
    const unassignedProductMappings: ProductMapping[] = [];
    this.loadingMappings = true;
    this.productMappingResource.get(parseInt(this.fiId, 10)).then((mappings:ProductMapping[]) => {
      mappings.forEach((mapping:ProductMapping) => {
        if (mapping.kasasaPowered && !this.hasAssignedProduct(mapping)) {
          unassignedProductMappings.push(mapping);
        }
      });
      this.unassignedProductMappings = unassignedProductMappings;
    }).catch((error:any) => {
  	  this.showError(error);
  	}).finally(() => {
	    this.loadingMappings = false;
  	});
  }

  loadProducts(editMode:boolean) : Promise<void> {
    this.loadingCurrentProducts = true;
    return this.productResource.getCurrentForConfigVersion(String(this.fiId))
      .then((result:Product[]) => {
        this.allCurrentProductsInCoreVersion = result;
        this.buildTableItems();
        this.loadedFiId = this.fiId;

        if (editMode) {
          this.editProduct();
        } else {
          this.updateWithLatestVersionedProducts();
        }
      })
      .catch((error:any) => {
        this.showError(error);
      })
      .finally(() => {
        this.loadingCurrentProducts = false;
      });
  }

  async loadVersionedProducts(): Promise<void> {
    this.loadingVersionProducts = true;
    await this.productResource
      .getAllMappedForConfigVersion(String(this.fiId))
      .then((result:Map<number, Product[]>) => {
        this.mappedProducts = result;
        this.loadingVersionProducts = false;
      })
      .catch((error:any) => {
        this.showError(error);
      })
      .finally(() => {
        this.loadingVersionProducts = false;
      });
  }

  isLoading() : boolean {
    return this.loadingVersionProducts || this.loadingCurrentProducts || this.loadingMappings;
  }

  thereAreUnassignedProductMappings() {
    return this.unassignedProductMappings && this.unassignedProductMappings.length > 0;
  }

  buildTableItems() {
    this.tableItems = [];
    this.allCurrentProductsInCoreVersion.forEach((product:Product) => {
      this.tableItems.push({
        product,
        setup: this.getProductSetupStatus(product),
      });
    });
  }

  getProductSetupStatus(product:Product) : boolean | null {
    let status:boolean|null = null;
    if (this.productValidationResults) {
      for (let i = 0; i < this.productValidationResults.length; i++) {
        if (this.productValidationResults[i].productId === product.id) {
          status = !ProductResource.productHasIssues(this.productValidationResults[i]);
          break;
        }
      }
    }

    return status;
  }

  editProduct() {
    const productId: string | undefined = this.$route.params.productId;
    this.productSettingsDirty = false;
    this.levelRewardsDirty = false;

    if (productId && productId.length > 0) {
      for (let i = 0; i < this.allCurrentProductsInCoreVersion.length; i++) {
        if (this.allCurrentProductsInCoreVersion[i].id === parseInt(productId, 10)) {
          this.selectedProduct = this.allCurrentProductsInCoreVersion[i];
          break;
        }
      }

      if (this.selectedProduct === null) {
        this.setError(`No product with id ${productId} found.`);
        this.$router.push({ name: 'products', params: { clientId: this.$route.params.clientId } });
      } else {
        this.updateWithLatestVersionedProducts();
        this.productHeaderItems[2].text = this.selectedProduct.productMapping.fiProductName;
      }
    } else {
      this.selectedProduct = this.createEmptyProduct();
      this.productHeaderItems[2].text = 'New Product';
      this.selectedTab = 0;
      this.updateWithLatestVersionedProducts();
      this.onSetEditable();
    }

    this.title = this.productHeaderItems[2].text;
  }

  updateWithLatestVersionedProducts() {
    if (this.selectedProduct && this.selectedProduct.productMapping) {
      const mapId: number = this.selectedProduct.productMapping.externalProductMappingId!;
      this.versionedProducts = this.mappedProducts.get(mapId)!;
    } else {
      this.versionedProducts = [];
    }
    this.currentDesignIndex = this.getCurrentDesignIndex();
  }

  onCreateNewProductVersion() {
    let product: Product;
    if (this.selectedProduct) {
      product = ProductResource.deepCopy(this.selectedProduct);
      product.id = undefined;
      product.designStartDate = DateTool.todayBackendDate();
      product.hasAssociatedResults = false;
    } else {
      product = this.createEmptyProduct();
    }
    this.isEditable = true;
    this.createProductPressed = true;
    this.selectedProduct = product;
    this.productHeaderItems[2].text = 'New Product';
    this.selectedTab = 0;
    this.onSetEditable();
  }

  getUniqueCoreProductCodesForProduct = (coreAccountProductTypes: CoreAccountProduct[]) => {
    const allCoreProductCodes = coreAccountProductTypes.map(a => a.coreProductCode);
    return Array.from(new Set(allCoreProductCodes));
  }

  getYesNo = (active: boolean) => {
    if (active) return 'Yes';
    return 'No';
  }

  getFriendlyFormattedDate = (date: string|null) => DateTool.toFriendlyFormatFromIsoFormatString(date)

  createEmptyProduct(): Product {
    const product: Product = ProductResource.createEmptyProduct();
    const atLeastOneOtherProductVersionExistsAlready = this.versionedProducts && this.versionedProducts.length > 0;
    if (this.configVersionDateRange && !atLeastOneOtherProductVersionExistsAlready) {
      product.designStartDate = DateTool.toIsoDateString(this.configVersionDateRange.startDate);
    }

    return product;
  }

  onEditProduct(product: Product) {
    this.$router.push({ name: 'product', params: { clientId: this.$route.params.clientId, productId: product.id!.toString() } });
  }

  onNewProduct() {
    this.$router.push({ name: 'newproduct', params: { clientId: this.$route.params.clientId } });
  }

  viewAllProducts() {
    this.$router.push({ name: 'products', params: { clientId: this.$route.params.clientId } });
  }

  isDirty(): boolean {
    const isEditingExistingProduct = this.selectedProduct !== null && this.selectedProduct.id;
    return this.levelRewardsDirty || this.productSettingsDirty || !isEditingExistingProduct;
  }

  productSaveOccurred(savedProduct: Product) {
    this.selectedProduct = savedProduct;
    this.createProductPressed = false;
    this.isEditable = false;

    this.loadVersionedProducts().then(() => {
      this.loadProducts(false).then(() => { this.loadProductMappings(); });
      this.validateProducts();
    });
  }

  validateProducts() {
    this.productResource.validateProducts(this.fiId).then((results:ProductValidationResult[]) => {
      this.mutationProductValidationResults(results);
    }).catch((error:any) => {
      this.showError(error);
    });
  }

  beforeRouteLeave(to:Route, from:Route, next: Function) {
    const that = this;
    if (this.levelRewardsDirty || this.productSettingsDirty) {
      const message: string = this.productSettingsDirty ? 'There are unsaved changes to product settings. Are you sure you want to leave this page?'
        : 'There are unsaved changes to level rewards. Are you sure you want to leave this page?';

      const dialog = new Dialog('Confirm Navigation', message);
	    dialog.setAcceptLabel('OK')
	      .setDeclineLabel('Cancel');
	    this.$store.dispatch('notices/add', dialog).then((ok) => {
	      if (ok === 'accept') {
          that.productSettingsDirty = false;
          that.levelRewardsDirty = false;
          next();
        }
      });
    } else {
      next();
    }
  }

  showError(error: any) {
    if (error.response && error.response.data && error.response.data.entity) {
      this.setError(error.response.data.entity);
    } else {
      this.setError('An unexpected error occurred.');
    }
  }

  setError(message: string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  getDescription(product: Product) : string {
    let startString : string = '';
    const productIndex : number = this.getProductIndex(product);
    if (productIndex === this.currentDesignIndex) {
      startString = 'Current Settings Design';
    } else {
      startString = 'Settings Design';
    }
    if (this.versionedProducts.length === 1 || productIndex === 0) {
      return `${startString} - ${DateTool.toFriendlyFormatFromIsoFormatString(product.designStartDate)}`;
    }
    return `${startString} - ${DateTool.toFriendlyFormatFromIsoFormatString(product.designStartDate)} - ${DateTool.subtractOneDay(
      this.versionedProducts[productIndex - 1].designStartDate,
    )}`;
  }

  getCurrentDesignIndex() : number {
    for (let i = 0; i < this.versionedProducts.length; i++) {
      if (DateTool.parseDate(this.versionedProducts[i].designStartDate) < new Date()) {
        return i;
      }
    }
    return -1;
  }

  getProductIndex(product: Product) : number {
    const isEqual = (element : Product) => element === product;
    return this.versionedProducts.findIndex(isEqual);
  }

  hasAssignedProduct(mapping: ProductMapping): boolean {
    const externalProductMappingId: number|null = mapping.externalProductMappingId;
    if (externalProductMappingId !== null) {
      return !!this.mappedProducts.get(externalProductMappingId);
    }
    return false;
  }
}
</script>

<style lang="scss">
</style>
